import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SSTFApiClient } from "../services/api";

// SSTF が発行する GCS V4 signed URL の TTL は 600 秒（X-Goog-Expires=600）。
// 期限切れで img が壊れる前に proactive に再発行するため少し短い 8 分で回す。
const BASELINE_URL_REFRESH_INTERVAL_MS = 8 * 60 * 1000;

// presigned URL 取得の同時実行上限。SSTF 側のレートリミットと
// ブラウザの同一ホスト同時接続数を考慮した経験則。
const FETCH_CONCURRENCY = 8;

export interface BaselineFileIds {
  full?: string | null;
  target?: string | null;
}

export type RegressionBaseline = Record<string, BaselineFileIds>;

export interface BaselineUrls {
  full?: string;
  target?: string;
}

export type BaselineUrlMap = Record<string, BaselineUrls>;

/**
 * value.regression_baseline の {order: {full, target}} の file_id を
 * 既存の file API で signed URL に解決する。
 *
 * baseline 画像は別の test_result（前回 OK 結果 or testcase 添付）に属するため、
 * 現在の test_result の evidenceFiles からは引けない。controller が解析時に
 * file_id を value JSON に書き込むので、それを表示直前に signed URL に変換する。
 */
export function useBaselineUrls(
  regressionBaseline: RegressionBaseline | undefined,
  workspaceId: string,
  projectId: string,
  apiClient: SSTFApiClient | null
): BaselineUrlMap {
  const [urls, setUrls] = useState<BaselineUrlMap>({});

  // regressionBaseline は呼び出し側 (App.tsx) で selectedTest.value から
  // 取り出されるため、毎レンダーでオブジェクト参照が変わりがち。
  // 値ベースのキー（JSON 文字列）に正規化することで、内容が同じであれば
  // useEffect を再発火させないようにする（呼び出し側の memoize 漏れに対する防衛）。
  const baselineKey = useMemo(
    () => (regressionBaseline ? JSON.stringify(regressionBaseline) : ""),
    [regressionBaseline]
  );
  const baselineRef = useRef<RegressionBaseline | undefined>(regressionBaseline);
  baselineRef.current = regressionBaseline;

  const fetchUrls = useCallback(
    async (signal: { cancelled: boolean }) => {
      const baseline = baselineRef.current;
      if (!baseline || !apiClient || !workspaceId || !projectId) {
        return;
      }

      // (order, kind, fileId) のジョブ一覧を作る
      type Job = { order: string; kind: "full" | "target"; fileId: string };
      const jobs: Job[] = [];
      for (const [order, ids] of Object.entries(baseline)) {
        if (ids.full) jobs.push({ order, kind: "full", fileId: ids.full });
        if (ids.target) jobs.push({ order, kind: "target", fileId: ids.target });
      }
      if (jobs.length === 0) {
        setUrls({});
        return;
      }

      // 同一 fileId は 1 回だけ presigned URL を取得する。
      const uniqueIds = Array.from(new Set(jobs.map((j) => j.fileId)));
      const idToUrl = new Map<string, string>();

      // 1) BFF のバルクエンドポイントで 1 回にまとめて取得を試みる
      let bulkOk = false;
      try {
        const bulkResult = await apiClient.bulkGetFileDownloadUrls(
          workspaceId,
          projectId,
          uniqueIds
        );
        for (const [id, url] of Object.entries(bulkResult)) {
          if (url) idToUrl.set(id, url);
        }
        bulkOk = true;
      } catch (e) {
        console.warn(
          "baseline: bulk signed URL 取得に失敗、個別取得にフォールバック:",
          e
        );
      }

      // 2) フォールバック: 同時実行を絞った並列取得（簡易セマフォ）
      if (!bulkOk) {
        let cursor = 0;
        const workers: Promise<void>[] = [];
        for (let i = 0; i < Math.min(FETCH_CONCURRENCY, uniqueIds.length); i++) {
          workers.push(
            (async () => {
              while (true) {
                const idx = cursor++;
                if (idx >= uniqueIds.length || signal.cancelled) return;
                const fileId = uniqueIds[idx];
                try {
                  const url = await apiClient.getFileDownloadUrlForDisplay(
                    workspaceId,
                    projectId,
                    fileId
                  );
                  if (url) idToUrl.set(fileId, url);
                } catch (e) {
                  console.error(`baseline URL 取得失敗 fileId=${fileId}:`, e);
                }
              }
            })()
          );
        }
        await Promise.all(workers);
      }
      if (signal.cancelled) return;

      const result: BaselineUrlMap = {};
      for (const job of jobs) {
        const url = idToUrl.get(job.fileId);
        if (!url) continue;
        const entry = result[job.order] ?? (result[job.order] = {});
        entry[job.kind] = url;
      }
      setUrls(result);
    },
    [baselineKey, workspaceId, projectId, apiClient]
  );

  useEffect(() => {
    if (!baselineKey || !apiClient || !workspaceId || !projectId) {
      setUrls({});
      return;
    }
    const signal = { cancelled: false };
    fetchUrls(signal);
    // 署名付き URL の expire 前に proactive に refetch する。
    // selectedTest を長時間開きっぱなしでも baseline 画像が切れないように。
    const intervalId = window.setInterval(() => {
      fetchUrls(signal);
    }, BASELINE_URL_REFRESH_INTERVAL_MS);
    return () => {
      signal.cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [baselineKey, workspaceId, projectId, apiClient, fetchUrls]);

  return urls;
}
